import {
  Button,
  Checkbox,
  DateFormats,
  formatISODate,
  formatNumber,
  Inline,
  Modal,
  type ModalProps,
  Notification,
  notifySuccess,
  Spinner,
  Stack,
  Table,
  Typography,
} from '@omnipresentgroup/design-system';
import {
  useContractor,
  useContractorContractDeactivateMutation,
  useContractorContractOutstandingFees,
  useGetContractorsInvoices,
} from '@omnipresentgroup/domains';
import { ContractorInvoiceStatus } from '@omnipresentgroup/features';
import { toSentenceCase } from '@omnipresentgroup/helpers';
import {
  type ContractOutstandingFee,
  type ContractorInvoice,
  ContractorInvoiceStateEnum,
  OutstandingFeeStatusEnum,
} from '@omnipresentgroup/types';
import {
  createColumnHelper,
  getCoreRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { useEffect, useMemo, useState } from 'react';

import { CONTRACTOR_INVOICES_PATH } from '../../constants';

const INVOICES_EMPTY_ARRAY: ContractorInvoice[] = [];
const FEES_EMPTY_ARRAY: ContractOutstandingFee[] = [];

const CHECKBOX_ID = 'contractor-deactivation-confirmation-checkbox';

const MODAL_TITLE = 'Contractor deactivation';
const OPEN_INVOICE_ACTION_REQUIRED = 'Pay or do nothing';
const AWAITING_TRANSACTION_ACTION_REQUIRED = 'Cancel or wait to receive';
const INVOICES_NOTIFICATION_TITLE =
  'You have invoice(s) that need your attention before deactivating this contractor.';
const INVOICES_NOTIFICATION_DESC =
  'The open invoice(s) will be cancelled once the contractor is deactivated. For invoice(s) that are Awaiting transfer, please wait to receive the fund or cancel the payment before deactivating.';
const INVOICES_NOTIFICATION_LINK_TEXT = 'Contractor invoices';
const FEES_NOTIFICATION_TITLE =
  'You have outstanding fees payable to Omnipresent Group Ltd.';
const FEES_NOTIFICATION_DESC =
  'You will receive a separate invoice to settle the payment, but you may proceed to deactivate the contractor.';

type Props = {
  modalProps: Omit<ModalProps, 'children'>;
  handleCancel: () => void;
  handleSuccess?: () => void;
  contractorId: string;
};

export const ContractorDeactivationModal = ({
  modalProps,
  handleCancel,
  handleSuccess,
  contractorId,
}: Props) => {
  const [deactivateConfirmed, setDeactivateConfirmed] = useState(false);

  const { data: contractor, isLoading: isLoadingContractor } = useContractor({
    id: contractorId,
  });

  const { data: invoices, isLoading: isLoadingContractorInvoices } =
    useGetContractorsInvoices({
      contractorId,
    });

  const { data: outstandingFees, isLoading: isLoadingOutstandingFees } =
    useContractorContractOutstandingFees({
      contractorId,
      contractId: contractor?.contracts[0].id || '',
    });

  const {
    mutate: deactivateContractor,
    isPending: isDeactivatingContractor,
    isSuccess: isSuccessDeactivateContractor,
  } = useContractorContractDeactivateMutation({
    contractorId,
    contractId: contractor?.contracts[0].id || '',
  });

  useEffect(() => {
    if (isSuccessDeactivateContractor) {
      handleSuccess?.();
      notifySuccess({ title: 'Contractor deactivated successfully' });
    }
  }, [handleSuccess, isSuccessDeactivateContractor]);

  const hasAwaitingTransactions = useMemo(
    () =>
      invoices?.items.some(
        (invoice) => invoice.state === ContractorInvoiceStateEnum.IN_PROGRESS,
      ),
    [invoices],
  );

  const canDeactivate = useMemo(
    () => !hasAwaitingTransactions,
    [hasAwaitingTransactions],
  );

  const filteredInvoices = useMemo(
    () =>
      invoices?.items.filter(
        (invoice) =>
          invoice.state === ContractorInvoiceStateEnum.IN_PROGRESS ||
          invoice.state === ContractorInvoiceStateEnum.OPEN,
      ) || INVOICES_EMPTY_ARRAY,
    [invoices],
  );

  const filteredOustandingFees = useMemo(
    () =>
      outstandingFees?.filter(
        (fee) => fee.status === OutstandingFeeStatusEnum.OPEN,
      ) || FEES_EMPTY_ARRAY,
    [outstandingFees],
  );

  const ContractorInvoices = () => {
    /** Table Columns */
    const columnHelper = createColumnHelper<ContractorInvoice>();

    const columns = useMemo(
      () => [
        columnHelper.display({
          id: 'invoice',
          cell: (info) => info.row.original.invoiceRef,
          header: () => 'Invoice',
        }),
        columnHelper.display({
          id: 'state',
          cell: (info) => (
            <ContractorInvoiceStatus
              invoice={info?.row.original}
              statusOverride={{
                [ContractorInvoiceStateEnum.OPEN]: {
                  intent: 'warning',
                  text: 'Open',
                },
                [ContractorInvoiceStateEnum.IN_PROGRESS]: {
                  text: 'Awaiting transfer',
                },
              }}
            />
          ),
          header: () => 'Status',
        }),
        columnHelper.display({
          id: 'action',
          cell: (info) => {
            if (
              info.row.original.state !== ContractorInvoiceStateEnum.OPEN &&
              info.row.original.state !== ContractorInvoiceStateEnum.IN_PROGRESS
            ) {
              return '';
            }
            return info.row.original.state === ContractorInvoiceStateEnum.OPEN
              ? OPEN_INVOICE_ACTION_REQUIRED
              : AWAITING_TRANSACTION_ACTION_REQUIRED;
          },
          header: () => 'Action required',
        }),
      ],
      [columnHelper],
    );

    const table = useReactTable({
      data: filteredInvoices,
      columns: columns,
      getCoreRowModel: getCoreRowModel(),
    });

    return (
      <Table
        id={'contractor-invoices-summary'}
        table={table}
        showHeader={false}
      />
    );
  };

  const ContractOutstandingFees = () => {
    const columnHelper = createColumnHelper<ContractOutstandingFee>();

    const columns = useMemo(
      () => [
        columnHelper.display({
          id: 'fee',
          cell: (info) =>
            `${toSentenceCase(info.row.original.type)} - ${formatISODate(
              info.row.original.month,
              DateFormats.DateWithoutDay,
            )}`,
          header: () => 'Outstanding fees',
        }),
        columnHelper.display({
          id: 'amount',
          cell: (info) =>
            `${formatNumber({
              input: Number(info.row.original.amount),
              currency: info.row.original.currency,
            })}`,
          header: () => 'Amount',
        }),
      ],
      [columnHelper],
    );

    const table = useReactTable({
      data: filteredOustandingFees,
      columns: columns,
      getCoreRowModel: getCoreRowModel(),
    });

    return (
      <Table
        id={'contract-outstanding-fees-summary'}
        table={table}
        showHeader={false}
      />
    );
  };

  const isLoading = useMemo(
    () =>
      isLoadingContractor ||
      isLoadingContractorInvoices ||
      isLoadingOutstandingFees,
    [
      isLoadingContractor,
      isLoadingContractorInvoices,
      isLoadingOutstandingFees,
    ],
  );

  const showWiderModal = useMemo(
    () => filteredInvoices.length > 0 || filteredOustandingFees.length > 0,
    [filteredInvoices.length, filteredOustandingFees.length],
  );

  return (
    <Modal {...modalProps} span={showWiderModal ? 5 : 3}>
      <Modal.Header title={MODAL_TITLE} />
      <Modal.Body>
        {isLoading ? (
          <Inline justify="center" align="center" h={70}>
            <Spinner size="40" />
          </Inline>
        ) : (
          <Stack gap="32">
            {filteredInvoices.length > 0 && (
              <>
                <Notification
                  intent="warning"
                  title={INVOICES_NOTIFICATION_TITLE}
                  description={INVOICES_NOTIFICATION_DESC}
                  id={'invoices-notification'}
                  link={{
                    text: INVOICES_NOTIFICATION_LINK_TEXT,
                    to: CONTRACTOR_INVOICES_PATH,
                  }}
                />
                <ContractorInvoices />
              </>
            )}
            {canDeactivate && filteredOustandingFees.length > 0 && (
              <>
                <Notification
                  intent="info"
                  title={FEES_NOTIFICATION_TITLE}
                  description={FEES_NOTIFICATION_DESC}
                  id={'fees-notification'}
                />
                <ContractOutstandingFees />
              </>
            )}
            {canDeactivate && (
              <Inline gap="4">
                <Checkbox
                  id={CHECKBOX_ID}
                  checked={deactivateConfirmed}
                  onCheckedChange={() =>
                    setDeactivateConfirmed(
                      (previousChecked) => !previousChecked,
                    )
                  }
                />
                <Typography as="label" size="14" htmlFor={CHECKBOX_ID}>
                  I confirm that I want to deactivate{' '}
                  <Typography
                    as="span"
                    size="14"
                    weight="medium"
                  >{`${contractor?.firstName} ${contractor?.lastName}`}</Typography>
                </Typography>
              </Inline>
            )}
          </Stack>
        )}
      </Modal.Body>
      <Modal.Footer>
        <Inline justify="space-between" w="stretch">
          <Button
            variant="secondary"
            onClick={handleCancel}
            disabled={isDeactivatingContractor}
          >
            Cancel
          </Button>
          <Button
            disabled={!canDeactivate || !deactivateConfirmed}
            loading={isDeactivatingContractor}
            onClick={() => {
              deactivateContractor();
            }}
          >
            Deactivate
          </Button>
        </Inline>
      </Modal.Footer>
    </Modal>
  );
};
